import { z } from 'zod'
import { DateInputSchema } from '../time'
import { LatLngBoundingBoxSchema } from '../locations/latLng'
import { TeamIdSchema, UserIdSchema } from '../ids'
import { PaginatedInputSchema, PaginatedOutputSchema } from '../elasticApi'
import { WithArchivedFilterSchema } from '../archive'
import {
  RemoveArchivableSchema,
  ResponseSuccessSchema,
  apiRequest,
  apiResponses,
  type ResponseError,
  type ResponseSuccess,
} from '../api'
import { isEmpty } from '../../util/isEmpty'
import {
  AreaDataSchema,
  AreaIdSchema,
  AreaRunTypeSchema,
  AreaStateNameSchema,
  UnsavedAreaSchema,
  type AreaData,
} from './model'

export const AREA_LIST_STATS = ['totalAccounts', 'assignedAccounts', 'unassignedAccounts'] as const
export const AreaListStatSchema = z.enum(AREA_LIST_STATS)
export type AreaListStat = z.infer<typeof AreaListStatSchema>
export const AREA_LIST_STATS_MAX_SIZE = 100

/**
 * /area/get
 */
export const AreaGetInputSchema = apiRequest({
  areaId: AreaIdSchema,
})
export type AreaGetInput = z.infer<typeof AreaGetInputSchema>
export type AreaGetSuccess = ResponseSuccess & {
  area: AreaData
}
export type AreaGetOutput = AreaGetSuccess | ResponseError

export const AreaGetByIdsInputSchema = apiRequest({
  areaIds: z.array(AreaIdSchema),
})
export type AreaGetByIdsInput = z.infer<typeof AreaGetByIdsInputSchema>
export const [AreaGetByIdsSuccessSchema, AreaGetByIdsOutputSchema] = apiResponses({
  areas: z.array(AreaDataSchema),
})
export type AreaGetByIdsOutput = z.infer<typeof AreaGetByIdsOutputSchema>

/**
 * /area/list
 */
export const AreaListSortBySchema = z.enum(['order', 'name', 'status', 'lastRunAt', 'createdAt', 'updatedAt', 'areaId'])
export type AreaListSortBy = z.infer<typeof AreaListSortBySchema>
export const AreaListSortOrderSchema = z.enum(['asc', 'desc'])
export type AreaListSortOrder = z.infer<typeof AreaListSortOrderSchema>

// The area search reads only numbers here and drops a string bound, unlike the other DateInput ranges.
const AreaDateRangeSchema = z.object({
  startTime: DateInputSchema.optional().meta({
    description: 'Start of the range, inclusive, in UTC epoch milliseconds. A string is ignored.',
  }),
  endTime: DateInputSchema.optional().meta({
    description: 'End of the range, inclusive, in UTC epoch milliseconds. A string is ignored.',
  }),
})

export const AreaListFiltersSchema = z.object({
  userId: UserIdSchema.optional(),
  teamId: TeamIdSchema.optional().meta({
    description: 'Legacy single-team filter. Kept mostly for backwards compatibility and behaves the same as teamIds.',
  }),
  teamIds: TeamIdSchema.array().optional().meta({
    description: 'Preferred direct-team filter. Returns areas assigned exactly to any of these teams.',
  }),
  downlineTeamIds: z.array(TeamIdSchema).optional().meta({
    description:
      'Downline-inclusive team filter. Returns areas relevant to any of these teams or their downline teams.',
  }),
  query: z.string().optional(),
  boundingBox: LatLngBoundingBoxSchema.optional(),
  areaStates: AreaStateNameSchema.array().optional(),
  createdDate: AreaDateRangeSchema.optional(),
  lastAssignmentDate: AreaDateRangeSchema.optional(),
  expirationDate: AreaDateRangeSchema.optional(),
})
export type AreaListFilters = z.infer<typeof AreaListFiltersSchema>

export const AreaListInputSchema = apiRequest(
  {
    ...AreaListFiltersSchema.shape,
    sortBy: AreaListSortBySchema.optional(),
    sortOrder: AreaListSortOrderSchema.optional(),
    stats: AreaListStatSchema.array().optional().meta({
      description:
        'Account count fields to attach on each area. Ignored unless version is 2. Omitted or empty skips count queries. When set, size cannot exceed 100.',
    }),
    ...WithArchivedFilterSchema.shape,
    ...PaginatedInputSchema.shape,
  },
  { exposeVersion: true }
).superRefine((input, ctx) => {
  if (input.version !== 2 || isEmpty(input.stats)) return
  if (isEmpty(input.size) || input.size <= AREA_LIST_STATS_MAX_SIZE) return
  ctx.addIssue({
    code: 'custom',
    path: ['size'],
    message: `size cannot exceed ${AREA_LIST_STATS_MAX_SIZE} when stats is set`,
  })
})
export type AreaListInput = z.infer<typeof AreaListInputSchema>
export const [AreaListSuccessSchema, AreaListOutputSchema] = apiResponses({
  areas: AreaDataSchema.array(),
  ...PaginatedOutputSchema.shape,
})
export type AreaListSuccess = z.infer<typeof AreaListSuccessSchema>
export type AreaListOutput = z.infer<typeof AreaListOutputSchema>

/**
 * /area/add
 *
 * Requires the area:add permission and
 *
 * Current user must be one of the following:
 * - must be an owner or manager of the company
 * - must be an owner or manager of this teamId
 */
export const AreaAddInputSchema = apiRequest({
  ...UnsavedAreaSchema.shape,
})
export type AreaAddInput = z.infer<typeof AreaAddInputSchema>
export const [AreaAddSuccessSchema, AreaAddOutputSchema] = apiResponses({
  area: AreaDataSchema,
})
export type AreaAddSuccess = z.infer<typeof AreaAddSuccessSchema>
export type AreaAddOutput = z.infer<typeof AreaAddOutputSchema>

/**
 * /area/update
 * Has same permission requirements as area/add
 */
export const AreaUpdateInputSchema = apiRequest({
  area: AreaDataSchema,
})
export type AreaUpdateInput = z.infer<typeof AreaUpdateInputSchema>
export type AreaUpdateSuccess = AreaAddSuccess
export type AreaUpdateOutput = AreaUpdateSuccess | ResponseError

/**
 * /area/remove
 * Has same permission requirements as area/add
 */
export const AreaRemoveInputSchema = apiRequest({
  ...RemoveArchivableSchema.shape,
  areaId: AreaIdSchema,
})
export type AreaRemoveInput = z.infer<typeof AreaRemoveInputSchema>
export const AreaRemoveSuccessSchema = ResponseSuccessSchema
export type AreaRemoveSuccess = z.infer<typeof AreaRemoveSuccessSchema>
export type AreaRemoveOutput = AreaRemoveSuccess | ResponseError

/**
 * /area/assign
 */
export const AreaAssignInputSchema = apiRequest({
  areas: z.array(AreaDataSchema).optional(),
  areaListInput: AreaListInputSchema.optional(),
  runType: AreaRunTypeSchema,
  preserveDownlineAssignments: z.boolean().optional(),
})
export type AreaAssignInput = z.infer<typeof AreaAssignInputSchema>
export type AreaAssignSuccess = ResponseSuccess
export type AreaAssignOutput = AreaAssignSuccess | ResponseError
